/**
 * ESPN season projections, re-scored under the league's own settings and
 * merged onto the board as a second, independent projection column.
 *
 * The three sources on the board answer different questions: KeepTradeCut is a
 * trade market, JuiceSheets aggregates other people's projections, and this is
 * ESPN's own model. Where they disagree is the interesting part.
 *
 * We deliberately re-score from raw stat categories rather than reusing ESPN's
 * published total, which assumes their default PPR scoring — a half-PPR league
 * would be reading the wrong number for every pass catcher.
 */

import { canonicalTeam, indexPlayers, normalizeNameKey } from './playerMatch.js';

const ESPN_JSON = '/espn-projections.json';

/** Sleeper scoring keys mapped to the projected stat they multiply. */
const SCORING_MAP = [
  ['passYd', 'pass_yd', 0.04],
  ['passTd', 'pass_td', 4],
  ['passInt', 'pass_int', -2],
  ['rushYd', 'rush_yd', 0.1],
  ['rushTd', 'rush_td', 6],
  ['recYd', 'rec_yd', 0.1],
  ['recTd', 'rec_td', 6],
  ['rec', 'rec', 0.5],
  ['fumLost', 'fum_lost', -2],
];

/**
 * Fantasy points for a projected stat line under a league's scoring.
 *
 * Two-point conversions and return touchdowns are not modelled — they are not
 * in the projection feed. Measured against ESPN's own published PPR totals the
 * omission costs about 0.8 points on average (2.5 for quarterbacks, who throw
 * the conversions), applied near-uniformly, so it shifts totals slightly
 * without reordering players.
 */
export const scoreProjection = (stats, scoringSettings = {}) => {
  if (!stats) return null;

  let points = 0;
  for (const [statKey, scoringKey, fallback] of SCORING_MAP) {
    const value = Number(stats[statKey]) || 0;
    if (!value) continue;
    const weight = scoringSettings[scoringKey];
    points += value * (typeof weight === 'number' ? weight : fallback);
  }
  return Math.round(points * 10) / 10;
};

export const fetchEspnProjections = async () => {
  const response = await fetch(ESPN_JSON);
  if (!response.ok) {
    throw new Error(`Could not load ESPN projections (${response.status}).`);
  }
  const payload = await response.json();
  if (!Array.isArray(payload?.players) || !payload.players.length) {
    throw new Error('ESPN projections were empty.');
  }
  return payload;
};

let espnCache = null;

export const getEspnProjectionsCached = async () => {
  if (espnCache) return espnCache;
  espnCache = await fetchEspnProjections();
  return espnCache;
};

const matchKey = (name, position, team) =>
  `${normalizeNameKey(name)}|${position}|${canonicalTeam(team)}`;

/**
 * Index ESPN's rows for lookup by exact identity, falling back to name+position.
 */
export const indexEspnPlayers = (players) => {
  const byKey = new Map();
  const byNamePos = new Map();

  for (const p of players) {
    byKey.set(matchKey(p.name, p.position, p.team), p);
    const np = `${normalizeNameKey(p.name)}|${p.position}`;
    if (!byNamePos.has(np)) byNamePos.set(np, []);
    byNamePos.get(np).push(p);
  }

  return (row) => {
    const exact = byKey.get(matchKey(row.name, row.position, row.team));
    if (exact) return exact;
    // Players change teams between feeds; an unambiguous name+position is enough.
    const candidates = byNamePos.get(`${normalizeNameKey(row.name)}|${row.position}`) || [];
    return candidates.length === 1 ? candidates[0] : null;
  };
};

/**
 * Attach `espnProj` to every board row ESPN also projects.
 *
 * ESPN's feed covers QB/RB/WR/TE only, so this is additive in the same way the
 * JuiceSheets overlay is — untouched rows keep everything they had.
 */
export const mergeEspnProjections = (board, players, scoringSettings) => {
  const lookup = indexEspnPlayers(players);

  let matched = 0;

  const merged = board.map((row) => {
    const hit = lookup(row);
    if (!hit) return row;

    const espnProj = scoreProjection(hit.stats, scoringSettings);
    if (espnProj == null) return row;

    matched += 1;
    return {
      ...row,
      espnProj,
      espnStats: hit.stats,
      // Overall, cross-position numbers — the other feeds only carry
      // positional ranks, which cannot be compared across positions.
      adp: hit.adp ?? null,
      overallRank: hit.overallRank ?? null,
      auctionValue: hit.auctionValue ?? null,
    };
  });

  return { board: merged, matched, total: players.length };
};

/**
 * Gap between the two projection sources, as a share of the larger one — a
 * quick read on how much the models disagree about a player.
 */
export const projectionSpread = (row) => {
  if (row?.proj == null || row?.espnProj == null) return null;
  const diff = row.espnProj - row.proj;
  const base = Math.max(Math.abs(row.proj), Math.abs(row.espnProj), 1);
  return { diff: Math.round(diff * 10) / 10, ratio: diff / base };
};

/**
 * ESPN's published auction value, as a market check on ours.
 *
 * Not something to price with — it assumes ESPN's own budget, team count and
 * lineup, none of which is necessarily this league's. It is here so a row can
 * say where the room's number and ours part company, which is the only part of
 * somebody else's auction values that is worth anything.
 */
export const mergeMarketAuction = (board, players) => {
  const lookup = indexPlayers(players || []);

  let matched = 0;
  const merged = board.map((row) => {
    const hit = lookup(row);
    if (hit?.auctionValue == null) return row;
    matched += 1;
    return { ...row, marketAuction: hit.auctionValue };
  });

  return { board: merged, matched };
};
